import { api } from "./apiClient";

const xhrPut = (url, body, headers = {}, onProgress) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url, true);
    Object.entries(headers || {}).forEach(([name, value]) => {
      if (value !== undefined && value !== null) xhr.setRequestHeader(name, value);
    });
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress?.(event.loaded, event.total);
    };
    xhr.onerror = () => reject(new Error("Direct S3 upload failed due to a network error."));
    xhr.onabort = () => reject(new Error("Direct S3 upload was aborted."));
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve({ etag: xhr.getResponseHeader("ETag") });
      } else {
        reject(new Error(`Direct S3 upload failed (${xhr.status}).`));
      }
    };
    xhr.send(body);
  });

const retryRequest = async (fn, attempts = 3) => {
  let lastError;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (attempt + 1 < attempts) {
        await new Promise((resolve) => window.setTimeout(resolve, 250 * (attempt + 1)));
      }
    }
  }
  throw lastError;
};

const uploadMultipart = async (file, authorization, onProgress) => {
  const partSize = Number(authorization.part_size);
  const parts = authorization.parts || [];
  const loadedByPart = new Map();
  const completed = new Array(parts.length);
  let cursor = 0;

  const report = (partNumber, loaded) => {
    loadedByPart.set(partNumber, loaded);
    const totalLoaded = [...loadedByPart.values()].reduce((sum, value) => sum + value, 0);
    const percent = file.size ? Math.min(99, Math.round((totalLoaded / file.size) * 100)) : 99;
    onProgress?.(percent);
  };

  const worker = async () => {
    while (true) {
      const index = cursor;
      cursor += 1;
      if (index >= parts.length) return;
      const part = parts[index];
      const partNumber = Number(part.part_number);
      const start = (partNumber - 1) * partSize;
      const end = Math.min(file.size, start + partSize);
      const blob = file.slice(start, end);
      const response = await xhrPut(part.url, blob, {}, (loaded) => report(partNumber, loaded));
      if (!response.etag) {
        throw new Error("S3 did not expose the multipart ETag. Check the bucket CORS ExposeHeaders setting.");
      }
      loadedByPart.set(partNumber, blob.size);
      report(partNumber, blob.size);
      completed[index] = { part_number: partNumber, etag: response.etag };
    }
  };

  const concurrency = Math.min(4, Math.max(1, parts.length));
  await Promise.all(Array.from({ length: concurrency }, () => worker()));
  onProgress?.(100);
  return completed;
};

export const documentsAPI = {
  listByProject: (projectId) => api.get(`/projects/${projectId}/documents`),

  // Legacy multipart backend upload remains for the existing RFI/Submittal
  // Document Control tabs. Project Documents use uploadDirect below.
  upload: (projectId, formData) =>
    api.post(`/projects/${projectId}/documents`, formData, {
      timeout: 120000,
      headers: { "Content-Type": "multipart/form-data" },
    }),

  initiateDirectUpload: (projectId, payload) =>
    api.post(`/projects/${projectId}/documents/direct-upload/initiate`, payload),

  completeDirectMultipartUpload: (projectId, payload) =>
    api.post(`/projects/${projectId}/documents/direct-upload/complete-multipart`, payload),

  abortDirectMultipartUpload: (projectId, payload) =>
    api.post(`/projects/${projectId}/documents/direct-upload/abort`, payload),

  finalizeDirectUpload: (projectId, payload) =>
    api.post(`/projects/${projectId}/documents/direct-upload/finalize`, payload),

  uploadDirect: async (projectId, file, metadata = {}, onProgress) => {
    const contentType = file.type || "application/octet-stream";
    const base = {
      filename: file.name,
      content_type: contentType,
      file_size: file.size,
      inspection_id: metadata.inspection_id ?? null,
    };

    onProgress?.(0);
    const initiated = await documentsAPI.initiateDirectUpload(projectId, base);
    const authorization = initiated.data;
    let parts = null;

    try {
      if (authorization.mode === "multipart") {
        parts = await uploadMultipart(file, authorization, onProgress);
      } else {
        await xhrPut(
          authorization.url,
          file,
          authorization.headers || {},
          (loaded) => {
            const percent = file.size ? Math.min(99, Math.round((loaded / file.size) * 100)) : 99;
            onProgress?.(percent);
          },
        );
        onProgress?.(100);
      }
    } catch (error) {
      if (authorization.mode === "multipart" && authorization.upload_id) {
        try {
          await documentsAPI.abortDirectMultipartUpload(projectId, {
            object_key: authorization.object_key,
            upload_id: authorization.upload_id,
            inspection_id: base.inspection_id,
          });
        } catch (_) {
          // Best-effort cleanup; preserve the original upload error.
        }
      }
      throw error;
    }

    const finalizePayload = {
      ...base,
      object_key: authorization.object_key,
      document_name: metadata.document_name,
      document_type: metadata.document_type ?? null,
      category: metadata.category ?? null,
      version: metadata.version ?? null,
      status: metadata.status ?? "draft",
      rag_mode: metadata.rag_mode ?? "manual",
    };

    if (authorization.mode === "multipart") {
      try {
        await documentsAPI.completeDirectMultipartUpload(projectId, {
          object_key: authorization.object_key,
          upload_id: authorization.upload_id,
          inspection_id: base.inspection_id,
          parts,
        });
      } catch (completionError) {
        // Completion responses can be lost after S3 has already assembled the
        // object. Finalize is idempotent and HEAD-verifies the object, so use it
        // as the safe recovery path before deciding the multipart upload failed.
        try {
          return await retryRequest(
            () => documentsAPI.finalizeDirectUpload(projectId, finalizePayload),
            3,
          );
        } catch (_) {
          try {
            await documentsAPI.abortDirectMultipartUpload(projectId, {
              object_key: authorization.object_key,
              upload_id: authorization.upload_id,
              inspection_id: base.inspection_id,
            });
          } catch (__unused) {
            // Best effort only.
          }
          throw completionError;
        }
      }
    }

    return retryRequest(
      () => documentsAPI.finalizeDirectUpload(projectId, finalizePayload),
      3,
    );
  },

  delete: (projectId, documentId) =>
    api.delete(`/projects/${projectId}/documents/${documentId}`),

  // Link an existing project document to an inspection without re-uploading it.
  // The same document may be linked to multiple inspections.
  linkToInspection: (projectId, inspectionId, documentId) =>
    api.post(
      `/projects/${projectId}/inspections/${inspectionId}/documents/${documentId}`,
    ),

  // Remove only the inspection relationship; keep the file in Document Control.
  unlinkFromInspection: (projectId, inspectionId, documentId) =>
    api.delete(
      `/projects/${projectId}/inspections/${inspectionId}/documents/${documentId}`,
    ),

  indexForRag: (projectId, documentId, options = {}) =>
    api.post(
      `/projects/${projectId}/documents/${documentId}/rag/index`,
      options,
    ),

  indexForRagSemantic: (projectId, documentId, formData) =>
    api.post(
      `/projects/${projectId}/documents/${documentId}/rag/index-semantic`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    ),
};
